/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable no-nested-ternary */
import {
  Breadcrumbs,
  Carousel,
  CourseCard,
  CourseDescriptionSection,
  Modal,
  SignInForm,
  SignUpForm,
  Tag,
} from '@components';
import {
  convertMaterialsToString,
  convertMinutesToTimeString,
  convertTestToString,
} from '@helpers';
import {
  useNavigateWithOfflineMode,
  useScreenSize,
  useScrollToTop,
} from '@hooks';
import Net from '@pngs/SoldierLevelPage/Net.png';
import SoldierLevelMobile from '@pngs/SoldierLevelPage/SoldierLevelMobile.png';
import { courseApi, courseSlice } from '@store';
import { Flex, Image, theme, Typography } from '@styles';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  getCertificationModule,
  soldierLevelPageLayoutByVariant,
} from './SoldierLevelPage.layoutProps';

const SoldierLevelPage = () => {
  const dispatch = useDispatch();
  const { navigate } = useNavigateWithOfflineMode();
  const { i18n } = useTranslation();

  const [isModalVisible, setModalVisible] = useState(false);

  const { authModalState, isOnboardingPassed, jwtAuthenticationToken } =
    useSelector((state) => state.authSlice);

  const { courseId } = useParams();

  const { device, isMobile, isTabletLG, isTabletSM } = useScreenSize();
  const layout = soldierLevelPageLayoutByVariant[device || 'desktop'];

  const { course, refetch: refetchCourse } = courseApi.useGetCourseByIdQuery(
    { id: courseId },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data }) => ({
        course: data?.data,
      }),
    }
  );
  useEffect(() => {
    refetchCourse();
  }, [jwtAuthenticationToken, i18n.language]);

  useEffect(() => {
    if (course) {
      dispatch(courseSlice.actions.setCourseById(course));
    }
  }, [course]);

  const handleNavigateToModule =
    ({ path, state }) =>
    () => {
      navigate(path, { state });
    };

  const handleModalClose = () => {
    document.body.style.overflow = 'unset';
    setModalVisible(false);
  };

  const modalLayoutByState = {
    signIn: <SignInForm onSignInButtonClick={handleModalClose} />,
    signUp: <SignUpForm onSignUpButtonClick={handleModalClose} />,
  };

  useScrollToTop();

  return (
    <Flex flexDirection="column">
      <Modal
        isVisible={isModalVisible}
        modalContainerProps={{
          height: '100%',
          maxHeight: '618px',
        }}
        setModalVisible={setModalVisible}
        withBackdropNoise
      >
        {modalLayoutByState[authModalState]}
      </Modal>
      <Breadcrumbs containerProps={{ px: layout?.px }} />
      <Flex flexDirection="column" position="relative">
        <Image bottom={0} opacity={0.6} position="absolute" src={Net} />
        <Flex
          backgroundImage={
            isTabletLG
              ? SoldierLevelMobile
              : `https://geneva-learning-platform-resources.s3.eu-north-1.amazonaws.com/images/courses/course-${course?.id}-cover.png`
          }
          backgroundPosition="right"
          backgroundRepeat="no-repeat"
          backgroundSize="contain"
          flexDirection="column"
          gap={24}
          px={layout?.px}
          py={layout.title.py}
        >
          <Flex
            flexDirection="column"
            gap={8}
            width={isTabletSM ? '100%' : '60%'}
          >
            <Typography
              color={theme.colors.white[100]}
              variant={isTabletSM ? 'BN-bold-s64-lh55' : 'BN-bold-s123-lh104'}
            >
              {course?.title?.toUpperCase()}
            </Typography>
            <Typography
              color={theme.colors.white[60]}
              variant="MS-regular-s16-lh25"
            >
              {course?.description}
            </Typography>
          </Flex>
          {course && (
            <Flex gap={8}>
              <Tag
                content={convertMinutesToTimeString(course?.minutes_count)}
                variant="time"
              />
              <Tag
                content={convertMaterialsToString({
                  isCourse: true,
                  value: course?.materials_count,
                })}
                variant="material"
              />
              {convertTestToString(true) && (
                <Tag
                  content={i18n.t(convertTestToString(true))}
                  variant="test"
                />
              )}
            </Flex>
          )}
        </Flex>
        <Flex bg={theme.colors.white[10]} px={layout?.px} py={80}>
          <Typography
            color={theme.colors.white[100]}
            maxWidth={570}
            variant="MS-regular-s24-lh28"
          >
            {course?.learning_outcome}
          </Typography>
        </Flex>
      </Flex>
      <CourseDescriptionSection
        attitude={course?.attitude}
        containerProps={{ px: layout.px }}
        knowledge={course?.knowledge}
      />
      <Flex
        minHeight="1000px"
        overflow="hidden"
        pb={isTabletSM ? 150 : 80}
        pt={80}
      >
        {course && (
          <Carousel
            containerProps={{
              buttons: {
                bottomOffset: jwtAuthenticationToken ? -123 : -53,
                isMobile: isTabletSM,
                rightOffset: isMobile ? '20%' : isTabletSM ? '30%' : 0,
              },
              pl: layout.pl,
            }}
            items={
              course?.modules?.concat(
                getCertificationModule({ course, language: i18n.language })
              ) || []
            }
            openLoginModal={setModalVisible}
            renderItem={(item, index) => {
              const isPassed = item?.is_passed;
              const isAvailable = item?.is_available;
              const isLastAvailable =
                index === 0 || course?.modules?.[index - 1]?.is_available;
              const unlockTest = isPassed
                ? false
                : !(isAvailable || !isLastAvailable);
              const moduleId = unlockTest
                ? course?.modules?.[index - 1]?.id
                : item?.id;
              return (
                <CourseCard
                  key={item?.id}
                  completedLessonsCount={item?.completed_lessons_count}
                  cover={item?.image}
                  description={item?.description}
                  handleNavigate={handleNavigateToModule(
                    item?.certification
                      ? {
                          path: `/courses/${courseId}/certification`,
                          state: {
                            id: course?.test_id,
                          },
                        }
                      : {
                          path: `/courses/${courseId}/${moduleId}`,
                          state: {
                            finalTest: unlockTest,
                            pageTitle: item?.title,
                            restart: isPassed,
                          },
                        }
                  )}
                  isAvailable={isOnboardingPassed && isAvailable}
                  isLastAvailable={isOnboardingPassed && isLastAvailable}
                  isLoggedIn={!!jwtAuthenticationToken}
                  isPassed={isPassed}
                  itemClass="carousel-card-class"
                  materials={convertMaterialsToString({
                    isCourse: false,
                    value: item?.lessons_count,
                  })}
                  time={convertMinutesToTimeString(item?.minutes_count)}
                  title={item?.title}
                  totalLessonsCount={item?.lessons_count}
                />
              );
            }}
            title={i18n.t('Breadcrumbs.Modules')}
            withOnboardingWarning={!jwtAuthenticationToken}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default SoldierLevelPage;
